import './over-ons.scss'

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)')

const debounce = (fn, wait = 250) => {
	let timer
	return (...args) => {
		window.clearTimeout(timer)
		timer = window.setTimeout(() => fn(...args), wait)
	}
}

/**
 * Reveal a shape without the scroll animation (reduced motion / no-JS fallback).
 */
const showShapeStatic = (shape) => {
	const reveal = shape.querySelector('[id$="-reveal"]')
	const fill = shape.querySelector('[class$="-fill"]')
	if (reveal) reveal.style.strokeDashoffset = '0'
	if (fill) fill.style.opacity = '1'
	shape.style.opacity = '1'
}

const enableStatic = (section) => {
	section.classList.add('is-static')
	section.querySelectorAll('.o-overOns__shape').forEach(showShapeStatic)
}

class OverOns {
	constructor(section, gsap, ScrollTrigger, SplitText) {
		this.section = section
		this.gsap = gsap
		this.ScrollTrigger = ScrollTrigger
		this.SplitText = SplitText

		this.pin = section.querySelector('.o-overOns__pin')
		this.shapes = gsap.utils.toArray(section.querySelectorAll('.o-overOns__shape'))
		this.panels = gsap.utils.toArray(section.querySelectorAll('.o-overOns__panel'))

		if (!this.pin || !this.panels.length) return

		this.splits = []
		this.timeline = null
		this.build = this.build.bind(this)

		this.build()
		window.addEventListener('resize', debounce(this.build, 300))
		REDUCED_MOTION.addEventListener('change', (event) => {
			if (event.matches) {
				this.teardown()
				enableStatic(this.section)
			}
		})
	}

	shapeFor(panel) {
		return this.section.querySelector(`.o-overOns__shape[data-shape="${panel.dataset.shape}"]`)
	}

	teardown() {
		if (this.timeline) {
			if (this.timeline.scrollTrigger) this.timeline.scrollTrigger.kill()
			this.timeline.kill()
			this.timeline = null
		}
		this.splits.forEach((split) => split.revert())
		this.splits = []
		this.gsap.set(this.panels, { clearProps: 'all' })
	}

	build() {
		const { gsap } = this
		this.teardown()

		const panelData = this.panels.map((panel) => {
			const titleEl = panel.querySelector('.o-overOns__title')
			const contentEl = panel.querySelector('.o-overOns__content')
			const titleSplit = new this.SplitText(titleEl, { type: 'words', wordsClass: 'o-overOns__word' })
			const contentSplit = new this.SplitText(contentEl, { type: 'lines', linesClass: 'o-overOns__line' })
			this.splits.push(titleSplit, contentSplit)

			return {
				panel,
				shape: this.shapeFor(panel),
				words: titleSplit.words,
				lines: contentSplit.lines,
			}
		})

		// Initial states.
		gsap.set(this.panels, { autoAlpha: 0 })
		gsap.set(this.shapes, { autoAlpha: 1 })
		this.shapes.forEach((shape) => {
			const reveal = shape.querySelector('[id$="-reveal"]')
			const fill = shape.querySelector('[class$="-fill"]')
			if (reveal) gsap.set(reveal, { strokeDashoffset: 100 })
			if (fill) gsap.set(fill, { opacity: 0 })
		})
		panelData.forEach(({ words, lines }) => {
			gsap.set(words, { yPercent: 110, opacity: 0 })
			gsap.set(lines, { y: 34, opacity: 0 })
		})

		const timeline = gsap.timeline({
			defaults: { ease: 'power3.out' },
			scrollTrigger: {
				trigger: this.section,
				start: 'top top',
				end: () => '+=' + Math.round(window.innerHeight * 6.6),
				pin: this.pin,
				scrub: 1,
				markers:false,
				anticipatePin: 1,
				invalidateOnRefresh: true,
			},
		})

		panelData.forEach(({ panel, shape, words, lines }, index) => {
			const isLast = index === panelData.length - 1
			const reveal = shape && shape.querySelector('[id$="-reveal"]')
			const fill = shape && shape.querySelector('[class$="-fill"]')

			// First draw the outline as a line drawing (steady pace), then colour it
			// in once the outline is complete -- matching the original animation.
			if (reveal) timeline.fromTo(reveal, { strokeDashoffset: 100 }, { strokeDashoffset: 0, duration: 1.7, ease: 'none' }, index === 0 ? 0.2 : '>0.15')
			if (fill) timeline.fromTo(fill, { opacity: 0 }, { opacity: 1, duration: 0.7, ease: 'power1.out' }, '>')

			// Bring in the copy: title words, then content lines, staggered.
			timeline.to(panel, { autoAlpha: 1, duration: 0.01 }, '>-0.25')
			timeline.to(words, { yPercent: 0, opacity: 1, duration: 0.6, stagger: 0.07 }, '<')
			timeline.to(lines, { y: 0, opacity: 1, duration: 0.6, stagger: 0.14 }, '<0.2')

			// Hold still so the reader can actually read it.
			timeline.to({}, { duration: 1.9 })

			// Move the copy out before the next state (shapes stay, cumulatively).
			if (!isLast) {
				timeline.to(lines, { y: -26, opacity: 0, duration: 0.4, stagger: 0.04 }, '>')
				timeline.to(words, { yPercent: -70, opacity: 0, duration: 0.4, stagger: 0.04 }, '<0.05')
				timeline.to(panel, { autoAlpha: 0, duration: 0.01 })
			}
		})

		this.timeline = timeline
	}
}

document.addEventListener('DOMContentLoaded', async () => {
	const sections = Array.from(document.querySelectorAll('[data-over-ons]'))
	if (!sections.length) return

	// Reduced motion: keep it readable and static, and skip loading GSAP entirely.
	if (REDUCED_MOTION.matches) {
		sections.forEach(enableStatic)
		return
	}

	// Load GSAP only where the block is present (kept out of the main bundle).
	const [{ default: gsap }, { ScrollTrigger }, { SplitText }] = await Promise.all([
		import('gsap'),
		import('gsap/ScrollTrigger'),
		import('gsap/SplitText'),
	])

	gsap.registerPlugin(ScrollTrigger, SplitText)

	sections.forEach((section) => new OverOns(section, gsap, ScrollTrigger, SplitText))
})
